import { useState } from "react";
import { Card, CardContent, CardMedia, Box, useTheme } from "@mui/material";
import { TaskerMessage, TaskerConfig } from "@t3/shared";
import { useFirebaseStorageUrl } from "@/context/FirebaseStorageUrlContext";
import { getStatusColor } from "./utils/statusHelpers";
import { ImageDialog } from "./ImageDialog";
import { ContactInfoSection } from "./ContactInfoSection";
import { StatusOnlySection } from "./StatusOnlySection";
import { MessageBodySection } from "./MessageBodySection";
import { RejectedReasonsSection } from "./RejectedReasonsSection";

interface TaskerMessageReviewCardProps {
  message: TaskerMessage;
  size?: string;
  showContactInfo?: boolean;
  userTaskerConfigs?: TaskerConfig[];
}

export const TaskerMessageReviewCard = ({
  message,
  size = "420px",
  showContactInfo = true,
  userTaskerConfigs = [],
}: TaskerMessageReviewCardProps) => {
  const theme = useTheme();
  const [isImageDialogOpen, setIsImageDialogOpen] = useState(false);
  const { url: mediaFileUrl } = useFirebaseStorageUrl(
    message.mediaFiles?.[0]?.url,
  );

  const handleImageClick = () => {
    setIsImageDialogOpen(true);
  };

  const handleImageDialogClose = () => {
    setIsImageDialogOpen(false);
  };

  return (
    <>
      <Card
        sx={{
          width: "100%",
          minWidth: 0,
          maxWidth: size === "100%" ? "100%" : size,
          height: size === "100%" ? "100%" : "auto",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          backgroundColor: theme.palette.background.paper,
          borderRadius: 2,
          border: "2px solid #aaa",
          boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
          transition: "all 0.2s ease",
          "&:hover": {
            boxShadow: "0 6px 16px rgba(0, 0, 0, 0.15)",
          },
        }}
      >
        <CardMedia
          component="img"
          sx={{
            width: "100%",
            height: size === "100%" ? "auto" : size,
            aspectRatio: size === "100%" ? "1" : "auto",
            objectFit: "cover",
            cursor: "pointer",
            flex: "0 0 auto",
            transition: "transform 0.2s ease",
            "&:hover": {
              transform: "scale(1.02)",
            },
          }}
          image={mediaFileUrl || ""}
          alt="Post image"
          onClick={handleImageClick}
        />

        {/* Status indicator bar */}
        <Box
          sx={{
            height: "3px",
            width: "100%",
            flex: "0 0 auto",
            background: `linear-gradient(90deg, ${theme.palette[getStatusColor(message.reviewStatus) as "success" | "error" | "warning"].main}, ${theme.palette[getStatusColor(message.reviewStatus) as "success" | "error" | "warning"].light})`,
          }}
        />
        <CardContent
          sx={{
            p: { xs: 2, sm: 2.5 },
            flex: "1 1 auto",
            display: "flex",
            flexDirection: "column",
            pb: { xs: 2, sm: 2.5 },
            "&:last-child": { pb: { xs: 2, sm: 2.5 } },
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", mb: 0 }}>
            <Box sx={{ flex: 1 }}>
              {showContactInfo ? (
                <ContactInfoSection
                  firstName={message.messageMeta.firstName}
                  lastName={message.messageMeta.lastName}
                  phoneNumber={message.phoneNumber}
                  city={message.messageMeta.city}
                  state={message.messageMeta.state}
                  reviewStatus={message.reviewStatus}
                  timestamp={message.timestamp}
                />
              ) : (
                <StatusOnlySection
                  reviewStatus={message.reviewStatus}
                  rewardOptions={message.rewardOptions}
                  userTaskerConfigs={userTaskerConfigs}
                  eligibleProgramIds={message.eligibleProgramIds}
                />
              )}
            </Box>
          </Box>

          <MessageBodySection
            body={message.message.body}
            isApproved={message.reviewStatus === "approved"}
          />

          <RejectedReasonsSection
            message={message}
            userTaskerConfigs={userTaskerConfigs}
          />
        </CardContent>
      </Card>

      <ImageDialog
        open={isImageDialogOpen}
        imageUrl={mediaFileUrl || ""}
        onClose={handleImageDialogClose}
        altText="Post image"
      />
    </>
  );
};
